import type { RootState } from '@generalTypes/rootStateTypes';
import { selectValidationInfoForNodeAside } from '@newStore/validation/validationSelectors';
import type { EditAsideIdentifier } from '@nodeTypeConfig/configTypes';
import { patchNodeAction } from '@store/actions/documentActions';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { set } from 'lodash';
import { any } from 'prop-types';
import type React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useCurrentEditNode } from '../../../hooks/UseCurrentEditNode';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useReadOnly } from '../../../hooks/UseReadonly';

import SimpleInput from './SimpleInput';

const AsideIdentifier: React.FC<{
  config: EditAsideIdentifier;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();

  const editHref = useEditHref();

  const currentEditNode = useCurrentEditNode();
  const initialValue = currentEditNode.identifiers?.[0] || '';

  const nodeReadonly = useReadOnly();
  const hasValidationErrors = useSelector(
    (state: RootState) =>
      selectValidationInfoForNodeAside(state, config.component, config.property).validationErrors
        .length > 0,
  );

  // Allow editing an otherwise read-only identifier while it has validation errors (e.g. a
  // duplicate code after moving a goal), unless the whole node is read-only.
  const isReadonly = nodeReadonly || (readonly && !hasValidationErrors);

  const change = (value: string) => {
    dispatch(
      patchNodeAction(getResourceKey(editHref), set({}, config.property, value), true, null, false),
    );
  };

  return (
    <SimpleInput
      defaultValue={initialValue}
      label={config.label}
      readOnly={isReadonly}
      onChange={change}
    />
  );
};

AsideIdentifier.displayName = 'AsideIdentifier';

export default AsideIdentifier;

AsideIdentifier.propTypes = {
  config: any,
  readonly: any,
};
